// src/pages/LookerDashboard.tsx
/**
 * /looker — Looker templates drawn with the app's own widgets, plus a chat
 * that reads the same payload. The server scopes every query; this page only
 * asks. Spec: docs/superpowers/specs/2026-10-08-looker-dashboard-design.md.
 *
 * @module pages/LookerDashboard
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useIsPlatformMaster } from '@/hooks/useIsPlatformMaster';
import { useChannelFilter } from '@/contexts/ChannelFilterContext';
import { AD_CHANNEL_IDS } from '@/services/channelFilter';
import { LOOKER_ADVISOR } from '@/config/agentProfiles';
import MediaChatBubble from '@/components/MediaChatBubble';
import { LookerTileGrid } from '@/components/looker/LookerTileGrid';
import { useLookerAppTiles } from '@/hooks/useLookerAppTiles';
import { useLookerBackfill } from '@/hooks/useLookerBackfill';
import { BackfillStatusPill } from '@/components/looker/BackfillStatusPill';
import { useLookerChannelFreshness } from '@/hooks/useLookerChannelFreshness';
import { useLookerCoverage } from '@/hooks/useLookerCoverage';
import { coverageLabelsFrom } from '@/services/looker/lookerCoverageLabels';
import { DataStatusPill } from '@/components/looker/DataStatusPill';
import { useCanAccess } from '@/hooks/useCanAccess';
import { useLookerLayout } from '@/hooks/useLookerLayout';
import { PRINT_FRAME_WIDTH_PX, usePrintFrame } from '@/hooks/usePrintFrame';
import { LookerPrintHeader } from '@/components/looker/LookerPrintHeader';
import { ExportMenu } from '@/components/looker/ExportMenu';
import { LookerTemplateAccessPanel } from '@/components/looker/LookerTemplateAccessPanel';
import { CurrencyPicker } from '@/components/looker/CurrencyPicker';
import { LOOKER_ACTION_CLASS, LOOKER_FIELD_CLASS, LOOKER_LABEL_CLASS, LookerFilterBar } from '@/components/looker/LookerFilterBar';
import { YDMPageHero } from '@/components/brand';
import { fetchLookerDashboard, fetchLookerTemplates } from '@/services/looker/lookerApi';
import { buildLookerChatContext } from '@/services/looker/lookerChatContext';
import { csvLabelsFrom, downloadDashboardCsv } from '@/services/looker/lookerCsv';
import type { LookerDashboardPayload, LookerScope, LookerTemplateSummary } from '@/services/looker/lookerTypes';

const ADMIN_ONLY: ['admin'] = ['admin'];
const LAYOUT_BUTTON =
  'inline-flex h-9 items-center rounded-lg border border-neutral-300 bg-white px-3 text-sm font-medium text-ink-900 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50';

/** HTTP status → the one message a viewer should read. */
const messageKeyFor = (status: number): string => {
  if (status === 401) return 'looker.sessionExpired';
  if (status === 503) return 'looker.notConnected';
  if (status === 403) return 'looker.forbidden';
  if (status === 404) return 'looker.templateMissing';
  if (status === 429) return 'looker.rateLimited';
  if (status === 400) return 'looker.badDateRange';
  return 'looker.unavailable';
};

const LookerDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { workspace, workspaces } = useWorkspace();
  const { isMaster } = useIsPlatformMaster(true);

  // Each workspace has its own templates, so a list is only good for the scope it was fetched for.
  const [templateList, setTemplateList] = useState<{ key: string; items: LookerTemplateSummary[] } | null>(null);
  const [templatesError, setTemplatesError] = useState<{ key: string; message: string } | null>(null);
  const [templatesVersion, setTemplatesVersion] = useState(0);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [viewAll, setViewAll] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});
  // The viewer's currency pick (FR-X-02); null = the server's default (the most spent).
  const [currency, setCurrency] = useState<string | null>(null);
  const [payload, setPayload] = useState<LookerDashboardPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // The filters of the last dashboard that loaded: a rejected date range must not take the bar away.
  const lastFilters = useRef<LookerDashboardPayload['template']['filters'] | null>(null);
  // The last currency list, so the picker stays (disabled) while a pick refetches; only for the scope it came from.
  const lastCurrencies = useRef<{ scopeKey: string; currencies: string[]; currency: string | null; unknownExcluded: boolean } | null>(null);
  const requestSeq = useRef(0);
  // Read by the template-list effect, which must not refetch when a template is picked.
  const templateIdRef = useRef(templateId);
  templateIdRef.current = templateId;

  // The sidebar workspace is the only workspace picker, for everyone. A master
  // may additionally switch to all workspaces combined. A page-level picker used
  // to override the sidebar, so the sidebar could name one workspace while the
  // tiles showed another (owner chose this, 2026-10-08).
  const workspaceId = workspace?.id ?? null;
  const showAll = isMaster && viewAll;
  const scope = useMemo<LookerScope | null>(
    () => (showAll ? { kind: 'all' } : workspaceId ? { kind: 'workspace', workspaceId } : null),
    [showAll, workspaceId],
  );

  // Picking a workspace in the sidebar means "show me that one".
  useEffect(() => {
    setViewAll(false);
    // A pick belongs to the scope it was made in; the new scope starts from the server's default.
    setCurrency(null);
  }, [workspaceId]);

  const scopeKey = scope ? (scope.kind === 'all' ? 'all' : scope.workspaceId) : null;
  // The list on screen may still be the previous scope's while this one loads; only the
  // current scope's list may start a dashboard request.
  const templates = templateList?.items ?? null;
  const templatesReady = templateList !== null && templateList.key === scopeKey;
  const templatesMessage = templatesError && templatesError.key === scopeKey ? templatesError.message : null;

  useEffect(() => {
    if (!scope || !scopeKey) return;
    let cancelled = false;
    void fetchLookerTemplates(scope).then((r) => {
      if (cancelled) return;
      if (!r.ok) {
        setTemplateList(null);
        return setTemplatesError({ key: scopeKey, message: messageKeyFor(r.status) });
      }
      setTemplatesError(null);
      setTemplateList({ key: scopeKey, items: r.data });
      // Keep the open template only if this scope has it too; otherwise open its first (the default).
      const cur = templateIdRef.current;
      const next = cur && r.data.some((tp) => tp.id === cur) ? cur : r.data[0]?.id ?? null;
      if (next !== cur) {
        setTemplateId(next);
        setFilters({});
        setCurrency(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [scope, scopeKey, templatesVersion]);

  // The app's remembered channel selection, the same one the Dashboard uses.
  // Sent as plain ids; the server applies it to the template's channel filter.
  // [] = every channel. Display order, and joined into a key, so the request
  // effect sees a stable value rather than a new array every render.
  const { selected: selectedChannels, filtering: channelFiltering } = useChannelFilter();
  const channelKey = channelFiltering ? AD_CHANNEL_IDS.filter((id) => selectedChannels.has(id)).join(',') : '';
  const channels = useMemo(() => (channelKey ? channelKey.split(',') : []), [channelKey]);

  // A template, scope, filter or channel change starts a new request; only the newest may land.
  useEffect(() => {
    const seq = ++requestSeq.current;
    if (!templateId || !scope) return;
    // Drop the old payload now: its tiles and chat context belong to the previous selection.
    setPayload(null);
    setLoading(true);
    setLoadError(null);
    // Until this scope's list is in, templateId may be one this scope does not have.
    if (!templatesReady) return;
    void fetchLookerDashboard({ templateId, scope, filters, channels, ...(currency ? { currency } : {}) }).then((r) => {
      if (seq !== requestSeq.current) return;
      setLoading(false);
      if (!r.ok) {
        setPayload(null);
        return setLoadError(messageKeyFor(r.status));
      }
      lastFilters.current = r.data.template.filters;
      lastCurrencies.current = {
        scopeKey: r.data.scope.kind === 'all' ? 'all' : r.data.scope.workspaceId,
        currencies: r.data.currencies, currency: r.data.currency, unknownExcluded: r.data.currencyUnknownExcluded,
      };
      setPayload(r.data);
    });
  }, [templateId, scope, filters, channels, currency, templatesReady]);

  // Chat identity comes from the payload on screen, never from the pickers.
  const payloadWorkspaceId = payload?.scope.kind === 'workspace' ? payload.scope.workspaceId : null;
  const workspaceName = useMemo(
    () => workspaces.find((w) => w.id === payloadWorkspaceId)?.name ?? null,
    [workspaces, payloadWorkspaceId],
  );
  const appStates = useLookerAppTiles(payload);
  const backfill = useLookerBackfill(payload);
  const syncStatus = useLookerChannelFreshness(payload);
  // Badges, chips and box 8's coverage; the chat and the file say the same (lookerTileNotes).
  const coverage = useLookerCoverage(payload);
  const coverageNotes = useMemo(() => ({ state: coverage, labels: coverageLabelsFrom(t), channels }), [coverage, t, channels]);
  // A file written while a box or a page-level read loads would carry "loading" notes or miss a line.
  const appLoading = Object.values(appStates).some((s) => s.status === 'loading')
    || backfill.status === 'loading' || syncStatus.status === 'loading' || coverage.status === 'loading';
  const chatContext = useMemo(
    () => (payload ? buildLookerChatContext(payload, workspaceName, t('looker.share'), appStates, backfill, syncStatus, coverageNotes) : null),
    [payload, workspaceName, t, appStates, backfill, syncStatus, coverageNotes],
  );

  // A rendered tile is asked for with exactly what its numbers were fetched with, currency included.
  const imageRequest = useMemo(
    () => (payload
      ? { templateId: payload.template.id, scope: payload.scope, filters, channels, ...(payload.currency ? { currency: payload.currency } : {}) }
      : undefined),
    [payload, filters, channels],
  );

  // Arranging is a workspace admin's (or a master's); the database enforces the same rule.
  const isWorkspaceAdmin = useCanAccess(ADMIN_ONLY);
  const layout = useLookerLayout(payload, isWorkspaceAdmin || isMaster);
  const printFrame = usePrintFrame();

  const chatScope = payload ? `${payload.template.id}:${payload.scope.kind === 'all' ? 'all' : payload.scope.workspaceId}` : '';

  const periodLine = payload?.period
    ? t('looker.periodLine', { start: payload.period.start, end: payload.period.end, prevStart: payload.period.previousStart, prevEnd: payload.period.previousEnd })
    : null;
  const barFilters = payload?.template.filters ?? (loadError === 'looker.badDateRange' ? lastFilters.current : null);
  const lastMoney = lastCurrencies.current && lastCurrencies.current.scopeKey === scopeKey ? lastCurrencies.current : null;
  const picker = payload
    ? { currencies: payload.currencies, value: payload.currency, unknownExcluded: payload.currencyUnknownExcluded }
    : lastMoney && { currencies: lastMoney.currencies, value: currency ?? lastMoney.currency, unknownExcluded: lastMoney.unknownExcluded };

  const eyebrow = showAll ? t('looker.allWorkspaces') : workspace?.name;

  // A master picks the sidebar workspace's templates; reachable even when it has none.
  const accessPanel = isMaster && !showAll && workspaceId
    ? <LookerTemplateAccessPanel workspaceId={workspaceId} onChanged={() => setTemplatesVersion((v) => v + 1)} />
    : null;

  if (templatesMessage) return <PageShell eyebrow={eyebrow} admin={accessPanel}><p className="text-sm text-neutral-800">{t(templatesMessage)}</p></PageShell>;
  if (!templates) return <PageShell eyebrow={eyebrow} admin={accessPanel}><p className="text-sm text-neutral-700">{t('looker.loading')}</p></PageShell>;
  if (templates.length === 0) return <PageShell eyebrow={eyebrow} admin={accessPanel}><p className="text-sm text-neutral-800">{t('looker.noTemplates')}</p></PageShell>;

  return (
    <PageShell eyebrow={eyebrow} admin={accessPanel}>
      {/* The Dashboard's control band: white, yellow left edge, controls inside. */}
      <section
        data-looker-controls
        className="print:hidden space-y-4 rounded-2xl border border-neutral-200 border-l-[5px] border-l-primary-500 bg-white px-5 py-4 shadow-sm"
      >
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <label className={LOOKER_LABEL_CLASS}>
              {t('looker.templateLabel')}
              <select
                aria-label={t('looker.templateLabel')}
                className={`${LOOKER_FIELD_CLASS} min-w-[12rem]`}
                value={templateId ?? ''}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                  setFilters({});
                  setCurrency(null);
                }}
              >
                {templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.title}</option>)}
              </select>
            </label>
            {barFilters && <LookerFilterBar filters={barFilters} onApply={setFilters} disabled={loading} />}
            {picker && (
              <CurrencyPicker
                currencies={picker.currencies}
                value={picker.value}
                unknownExcluded={picker.unknownExcluded}
                onChange={setCurrency}
                disabled={loading || !payload}
              />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {payload && <DataStatusPill latestDataDate={payload.freshness.latestDataDate} state={syncStatus} />}
            <BackfillStatusPill state={backfill} />
            <ExportMenu
              pdfDisabled={!payload || loading || layout.editing || printFrame.framing}
              onPdf={printFrame.start}
              // CSV waits for every box and both page-level reads.
              csvDisabled={!payload || loading || layout.editing || appLoading}
              onCsv={() => payload && downloadDashboardCsv(payload, appStates, layout.layout, workspaceName, csvLabelsFrom(t), channels, backfill, syncStatus, coverageNotes)}
            />
            {isMaster && (
              <label className="inline-flex items-center gap-2 text-sm font-medium text-ink-900">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-neutral-400 accent-ink-950"
                  checked={viewAll}
                  onChange={(e) => {
                    setViewAll(e.target.checked);
                    setCurrency(null);
                  }}
                />
                {t('looker.viewAllWorkspaces')}
              </label>
            )}
          </div>
        </div>

        {periodLine && <p className="text-xs text-neutral-700">{periodLine}</p>}

        {scope?.kind === 'all' && (
          <p role="status" className="rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-ink-950">{t('looker.allWorkspacesBanner')}</p>
        )}
      </section>

      {/* The controls band is not printed, so the compared range gets its own print-only line. */}
      {periodLine && <p data-looker-period-print className="hidden text-xs text-neutral-800 print:block">{periodLine}</p>}

      {loadError && <p className="text-sm text-neutral-800">{t(loadError)}</p>}
      {!loadError && loading && !payload && <p className="text-sm text-neutral-700">{t('looker.loading')}</p>}

      {payload && layout.canEdit && (
        <div className="flex flex-wrap items-center justify-end gap-2 print:hidden">
          {layout.editing && <p className="min-w-0 flex-1 basis-64 text-sm text-neutral-800">{t('looker.layout.hint')}</p>}
          {layout.error && <p role="alert" className="text-sm font-medium text-error-700">{t(layout.error)}</p>}
          {!layout.editing ? (
            <button type="button" className={LAYOUT_BUTTON} onClick={layout.start}>{t('looker.layout.arrange')}</button>
          ) : (
            <div className="flex shrink-0 gap-2">
              <button type="button" className={LAYOUT_BUTTON} disabled={layout.saving} onClick={() => void layout.reset()}>{t('looker.layout.reset')}</button>
              <button type="button" className={LAYOUT_BUTTON} disabled={layout.saving} onClick={layout.cancel}>{t('looker.layout.cancel')}</button>
              <button type="button" className={LOOKER_ACTION_CLASS} disabled={layout.saving} onClick={() => void layout.save()}>
                {t('looker.layout.save')}
              </button>
            </div>
          )}
        </div>
      )}

      {payload && (
        // While printing, the frame is narrowed to the sheet so every chart redraws at paper width.
        <div
          className="space-y-4 [-webkit-print-color-adjust:exact] [print-color-adjust:exact]"
          style={printFrame.framing ? { width: PRINT_FRAME_WIDTH_PX, maxWidth: '100%' } : undefined}
        >
          {/* Landscape A4 for this page only; the rule leaves with the page. */}
          <style>{'@page { size: A4 landscape; margin: 10mm; }'}</style>
          <LookerPrintHeader payload={payload} scopeLabel={eyebrow ?? ''} />
          <LookerTileGrid tiles={payload.tiles} layout={layout.layout} editing={layout.editing} onChange={layout.change} imageRequest={imageRequest} appStates={appStates}
            coverage={coverage} objective={payload.objective ?? null} />
        </div>
      )}

      {chatContext && payload && (
        <div className="print:hidden">
        <MediaChatBubble
          key={chatScope}
          mode="looker"
          lookerContext={chatContext}
          scope={chatScope}
          title={t('looker.chatTitle')}
          subtitle={t('looker.chatSubtitle')}
          guidedQuestions={LOOKER_ADVISOR.guidedQuestions}
        />
        </div>
      )}
    </PageShell>
  );
};

const PageShell: React.FC<{ eyebrow?: string; admin?: React.ReactNode; children: React.ReactNode }> = ({ eyebrow, admin, children }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 p-4 md:p-6 print:p-0">
      {/* The same title band every other page opens on; on paper the print header replaces it. */}
      <div className="print:hidden">
        <YDMPageHero eyebrow={eyebrow} headline={t('looker.title')} subtitle={t('looker.subtitle')} className="mb-2" />
      </div>
      {admin}
      {children}
    </div>
  );
};

export default LookerDashboard;
